import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, SvgIcon, Typography } from '@mui/material';

import howToDoughnutDe from 'src/assets/dashboard/howto_doughnut_de.svg?react';
import howToDoughnutEn from 'src/assets/dashboard/howto_doughnut_en.svg?react';
import howToStackedBarDe from 'src/assets/dashboard/howto_stackedbar_de.svg?react';
import howToStackedBarEn from 'src/assets/dashboard/howto_stackedbar_en.svg?react';
import useSurveySettings from 'src/hooks/useSurveySettings';
import { useCustomizing } from 'src/components/app/context/CustomizingContext';
import Modal from 'src/components/common/Modal';

import { styles } from './index.styles';

export const HELP_MODAL_SVG_ICON_TEST_ID = 'help-modal-svg-icon';

export interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const getImagesForLang = (
  language: string,
  customizingDoughnutImage?: string,
  customizingStackBarImage?: string,
): (string | FC)[] => {
  switch (language) {
    case 'de':
      return [
        customizingDoughnutImage || howToDoughnutDe,
        customizingStackBarImage || howToStackedBarDe,
      ];
    default:
      return [
        customizingDoughnutImage || howToDoughnutEn,
        customizingStackBarImage || howToStackedBarEn,
      ];
  }
};

const HelpModal: FC<HelpModalProps> = (props) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('dashboard');
  const { currentApp } = useCustomizing();
  const { minRespondentAmount } = useSurveySettings();

  const popupImages = useMemo(() => {
    // Get image, that is currently set in customizing.
    const customizingDoughnutImage = currentApp?.help_image_doughnut_url;
    const customizingStackBarImage = currentApp?.help_image_stack_bar_url;

    // Get array of images, fallback to defaults if customizings are not set.
    return getImagesForLang(
      language.substring(0, 2).toLowerCase(),
      customizingDoughnutImage,
      customizingStackBarImage,
    );
  }, [language]);

  return (
    <Modal
      className="DashboardHelpModal-root"
      headline={t('Labels.HowToReadEosDashboard')}
      {...props}
    >
      <Grid container spacing={4}>
        {popupImages.map((popupImage, index) =>
          typeof popupImage === 'string' ? (
            <Grid key={index} item xs={12} md={6}>
              <img src={popupImage} alt={t('Labels.HowToReadEosDashboard')} />
            </Grid>
          ) : (
            <Grid key={index} item xs={12} md={6}>
              <SvgIcon
                component={popupImage}
                inheritViewBox
                sx={styles.svgIcon}
                data-test-id={`${HELP_MODAL_SVG_ICON_TEST_ID}-${language}`}
              />
            </Grid>
          ),
        )}
      </Grid>
      <Box sx={{ wordWrap: 'break-word' }}>
        <Typography sx={{ paddingBottom: 4 }}>
          {t('HowToReadHelpText')}
          {' ('}
          <a
            href="https://dpdhl.sharepoint.com/sites/eos_hub/SitePages/Contacts.aspx"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://dpdhl.sharepoint.com/sites/eos_hub/SitePages/Contacts.aspx
          </a>
          )
        </Typography>

        <Typography sx={{ paddingBottom: 4 }}>
          {t('NegSelHelpText', { minRespondentAmount })}
        </Typography>
      </Box>
    </Modal>
  );
};

export default HelpModal;
